import type {
  Arrival,
  CalendarItem,
  Cohort,
  Contact,
  File,
} from '@efdigital/ll-addon-data';
import { DataTypeContent } from '@efdigital/ll-addon-data/data-types';
import { debounce } from '@efdigital/ll-addon-ui/modifiers/debounce';
import type { EditingContext } from '@efdigital/ll-admin-v2/components/edit-context.gts';
import { EditRecord } from '@efdigital/ll-admin-v2/components/edit-record.gts';
import { setAsyncHasMany } from '@efdigital/ll-admin-v2/data/async-setter';
import { deepRollback } from '@efdigital/ll-admin-v2/data/deep-rollback';
import { createDefaultArrival } from '@efdigital/ll-admin-v2/routes/programs/program/cohort/arrivals-defaults';
import { fn } from '@ember/helper';
import { on } from '@ember/modifier';
import { service } from '@ember/service';
import Component from '@glimmer/component';
import { tracked } from '@glimmer/tracking';
import type { Store } from '@warp-drive/core';
import { Await } from '@warp-drive/ember';

import { InputDateOnly, InputDateTime } from '../input-date.gts';
import CalendarItemsPicker from '../pickers/calendar-items-picker.gts';
import { ContactPicker } from '../pickers/contact-picker.gts';
import type { FilePickerResult } from '../pickers/file-picker.gts';
import FilePicker from '../pickers/file-picker.gts';
import ImagePickerBig from '../pickers/image-picker-big.gts';

function getIds(items: { id?: string | null }[]): string[] {
  return items.map((item) => item.id ?? '');
}

interface EditCohortArrivalsSignature {
  Args: {
    cohort: Cohort;
    arrival: Arrival;
    context: EditingContext;
  };
}

export class EditCohortArrivals extends Component<EditCohortArrivalsSignature> {
  @service declare store: Store;
  @tracked private _arrival: Arrival | null = null;

  get arrival(): Arrival {
    return this._arrival ?? this.args.arrival;
  }

  revertArrival = () => {
    if (this.arrival.isNew) {
      // Unload the unsaved record and replace it with a fresh one so the form
      // resets to blank — consistent with cancel behaviour on existing records.
      this.store.unloadRecord(this.arrival);
      this._arrival = createDefaultArrival(this.store, this.args.cohort);
    } else {
      deepRollback(this.arrival, this.store);
    }
  };

  setCodeword = (event: Event) => {
    const target = event.target as HTMLInputElement;
    this.arrival.codeword = target.value;
  };

  setTitle = (event: Event) => {
    const target = event.target as HTMLInputElement;
    this.arrival.title = target.value;
  };

  setLocation = (event: Event) => {
    const target = event.target as HTMLSelectElement;
    this.arrival.location = target.value;
  };

  setStarts = (value: string) => {
    this.arrival.starts = value;
  };

  setEnds = (value: string) => {
    this.arrival.ends = value;
  };

  setDisplayDate = (event: Event) => {
    const target = event.target as HTMLInputElement;
    this.arrival.displayDate = target.value;
  };

  setAccessCode = (event: Event) => {
    const target = event.target as HTMLInputElement;
    this.arrival.accessCode = target.value || null;
  };

  setCheckIn = (value: string) => {
    this.arrival.checkIn = value || null;
  };

  setCheckOut = (value: string) => {
    this.arrival.checkOut = value || null;
  };

  setMarkdown = (event: Event) => {
    const target = event.target as HTMLTextAreaElement;
    this.arrival.markdown = target.value;
  };

  setHeroImage = (url: string) => {
    this.arrival.heroImage = url || null;
  };

  setAccommodationNote = (event: Event) => {
    const target = event.target as HTMLTextAreaElement;
    this.arrival.accommodationNote = target.value || null;
  };

  toggleShowFeature = (feature: string) => {
    return (event: Event) => {
      const target = event.target as HTMLInputElement;
      const currentShow = this.arrival.show || [];
      const newShow = target.checked
        ? currentShow.includes(feature as (typeof this.arrival.show)[number])
          ? currentShow
          : [...currentShow, feature]
        : currentShow.filter((f) => f !== feature);
      this.arrival.show = newShow as typeof this.arrival.show;
    };
  };

  // Files picker handlers
  handleFileSelect = async (result: FilePickerResult) => {
    const file = result.item as File;
    const files = await this.arrival.files;
    if (!files.find((f) => f.id === file.id)) {
      setAsyncHasMany(this.arrival, 'files', [...files, file]);
    }
  };

  removeFile = async (file: File) => {
    const files = await this.arrival.files;
    setAsyncHasMany(
      this.arrival,
      'files',
      files.filter((f) => f.id !== file.id)
    );
  };

  // Calendar items picker handlers
  handleCalendarItemsSelect = async (calendarItems: CalendarItem[]) => {
    const current = await this.arrival.calendarItems;
    const newItems = calendarItems.filter(
      (item) => !current.find((c) => c.id === item.id)
    );
    setAsyncHasMany(this.arrival, 'calendarItems', [...current, ...newItems]);
  };

  removeCalendarItem = async (item: CalendarItem) => {
    const calendarItems = await this.arrival.calendarItems;
    setAsyncHasMany(
      this.arrival,
      'calendarItems',
      calendarItems.filter((c) => c.id !== item.id)
    );
  };

  // Contacts picker handlers
  handleContactSelect = async (contact: Contact) => {
    const contacts = await this.arrival.contacts;
    if (!contacts.find((c) => c.id === contact.id)) {
      setAsyncHasMany(this.arrival, 'contacts', [...contacts, contact]);
    }
  };

  removeContact = async (contact: Contact) => {
    const contacts = await this.arrival.contacts;
    setAsyncHasMany(
      this.arrival,
      'contacts',
      contacts.filter((c) => c.id !== contact.id)
    );
  };

  get heroImageValue(): string {
    return this.arrival.heroImage ?? '';
  }

  get checkInValue(): string {
    return this.arrival.checkIn ?? '';
  }

  get checkOutValue(): string {
    return this.arrival.checkOut ?? '';
  }

  get cohortId(): string {
    return this.args.cohort.id ?? '';
  }

  get startingFolderId(): string {
    const clientId = this.args.cohort.client?.id || '';
    return `${DataTypeContent.Folder}:|${clientId}|Cohorts|${this.cohortId}`;
  }

  get navigationFolders() {
    const clientId = this.args.cohort.client?.id || '';
    return [
      {
        id: `|${clientId}|Cohorts|${this.cohortId}`,
        label: 'Cohort',
        help: 'Files for this cohort',
      },
      { id: '|global', label: 'Global Files', help: 'Files for all clients' },
    ];
  }

  isFeatureShown = (feature: string) => {
    return (
      this.arrival.show?.includes(
        feature as (typeof this.arrival.show)[number]
      ) || false
    );
  };

  formatContactName = (contact: Contact): string => {
    return [contact.title, contact.firstname, contact.lastname, contact.suffix]
      .filter(Boolean)
      .join(' ');
  };

  <template>
    <EditRecord
      @editLabel="Edit Arrivals Information"
      @item={{this.arrival}}
      @context={{@context}}
      @maxDepth={{1}}
      @revert={{this.revertArrival}}
      ...attributes
    >
      <:form as |uniqueId isReadOnly|>
        <fieldset disabled={{isReadOnly}} class="mb-4">
          <legend class="gud-eyebrow">Basic Information</legend>

          <div class="mb-3">
            <label for="{{uniqueId}}-codeword" class="form-label required">
              Codeword
            </label>
            <input
              type="text"
              class="form-control"
              id="{{uniqueId}}-codeword"
              value={{this.arrival.codeword}}
              placeholder="Codeword"
              required
              {{debounce this.setCodeword}}
            />
            <div class="form-text">
              The unique codeword required to access the arrivals information
              page.
            </div>
          </div>

          <div class="mb-3">
            <label for="{{uniqueId}}-accessCode" class="form-label">
              Access Code
            </label>
            <input
              type="text"
              class="form-control"
              id="{{uniqueId}}-accessCode"
              value={{this.arrival.accessCode}}
              placeholder="e.g. door code or room number"
              {{debounce this.setAccessCode}}
            />
            <div class="form-text">
              Optional: A secondary code shown to participants after login (e.g.
              a room or door code).
            </div>
          </div>

          <div class="mb-3">
            <label for="{{uniqueId}}-title" class="form-label required">
              Title
            </label>
            <input
              type="text"
              class="form-control"
              id="{{uniqueId}}-title"
              value={{this.arrival.title}}
              placeholder="Title"
              required
              {{debounce this.setTitle}}
            />
          </div>

          <div class="mb-3">
            <label class="form-label">Hero Image</label>
            <ImagePickerBig
              @value={{this.heroImageValue}}
              @onChange={{this.setHeroImage}}
              @sizeHint="cohort-icon"
              @defaultTitle={{this.arrival.title}}
              @readonly={{isReadOnly}}
              @required={{false}}
            />
            <div class="form-text">
              Optional: Custom banner image. Defaults to the standard Ashridge
              program image if not set.
            </div>
          </div>

          <div class="mb-3">
            <label for="{{uniqueId}}-location" class="form-label required">
              Location
            </label>
            <select
              class="form-select"
              id="{{uniqueId}}-location"
              required
              {{debounce this.setLocation}}
            >
              <option value="ashridge-house">Ashridge House</option>
            </select>
          </div>
        </fieldset>

        <fieldset disabled={{isReadOnly}} class="mb-4">
          <legend class="gud-eyebrow">Dates</legend>

          <div class="row">
            <div class="col-md-6 mb-3">
              <label for="{{uniqueId}}-starts" class="form-label required">
                Start Date
              </label>
              <InputDateOnly
                @value={{this.arrival.starts}}
                @onChange={{this.setStarts}}
                class="form-control"
                id="{{uniqueId}}-starts"
                required
              />
            </div>

            <div class="col-md-6 mb-3">
              <label for="{{uniqueId}}-ends" class="form-label required">
                End Date
              </label>
              <InputDateOnly
                @value={{this.arrival.ends}}
                @onChange={{this.setEnds}}
                class="form-control"
                id="{{uniqueId}}-ends"
                required
              />
            </div>
          </div>

          <div class="mb-3">
            <label for="{{uniqueId}}-displayDate" class="form-label">
              Display Date
            </label>
            <input
              type="text"
              class="form-control"
              id="{{uniqueId}}-displayDate"
              value={{this.arrival.displayDate}}
              placeholder="Display Date"
              {{debounce this.setDisplayDate}}
            />
            <div class="form-text">
              Human-readable date text like "15-17 March 2026".
            </div>
          </div>

          <div class="row">
            <div class="col-md-6 mb-3">
              <label for="{{uniqueId}}-checkIn" class="form-label">
                Check-in Time
              </label>
              <InputDateTime
                @value={{this.checkInValue}}
                @onChange={{this.setCheckIn}}
                class="form-control"
                id="{{uniqueId}}-checkIn"
              />
            </div>

            <div class="col-md-6 mb-3">
              <label for="{{uniqueId}}-checkOut" class="form-label">
                Check-out Time
              </label>
              <InputDateTime
                @value={{this.checkOutValue}}
                @onChange={{this.setCheckOut}}
                class="form-control"
                id="{{uniqueId}}-checkOut"
              />
            </div>
          </div>
        </fieldset>

        <fieldset disabled={{isReadOnly}} class="mb-4">
          <legend class="gud-eyebrow">Content</legend>

          <div class="mb-3">
            <label for="{{uniqueId}}-markdown" class="form-label required">
              Welcome Message (Markdown)
            </label>
            <textarea
              class="form-control font-monospace"
              id="{{uniqueId}}-markdown"
              rows="8"
              placeholder="Markdown"
              required
              {{debounce this.setMarkdown}}
            >{{this.arrival.markdown}}</textarea>
            <div class="form-text">
              Markdown-formatted welcome message and arrival instructions.
            </div>
          </div>

          <div class="mb-3">
            <label for="{{uniqueId}}-accommodationNote" class="form-label">
              Accommodation Note
            </label>
            <textarea
              class="form-control"
              id="{{uniqueId}}-accommodationNote"
              rows="4"
              placeholder="A bedroom at Ashridge has been reserved for you..."
              {{debounce this.setAccommodationNote}}
            >{{this.arrival.accommodationNote}}</textarea>
            <div class="form-text">
              Optional: Custom note shown at the top of the accommodation page.
              Leave blank to use the default text.
            </div>
          </div>
        </fieldset>

        <fieldset disabled={{isReadOnly}} class="mb-4">
          <legend class="gud-eyebrow">Display Features</legend>

          <p class="form-text mb-3">
            Select which features to display on the arrivals page.
          </p>

          <div class="row">
            <div class="col-md-4">
              <div class="form-check mb-2">
                <input
                  type="checkbox"
                  class="form-check-input"
                  id="{{uniqueId}}-show-map"
                  checked={{this.isFeatureShown "map"}}
                  {{debounce (this.toggleShowFeature "map")}}
                />
                <label for="{{uniqueId}}-show-map" class="form-check-label">
                  Map of Ashridge House
                </label>
              </div>

              <div class="form-check mb-2">
                <input
                  type="checkbox"
                  class="form-check-input"
                  id="{{uniqueId}}-show-wifi"
                  checked={{this.isFeatureShown "wifi"}}
                  {{debounce (this.toggleShowFeature "wifi")}}
                />
                <label for="{{uniqueId}}-show-wifi" class="form-check-label">
                  Wi-Fi Access
                </label>
              </div>
            </div>

            <div class="col-md-4">
              <div class="form-check mb-2">
                <input
                  type="checkbox"
                  class="form-check-input"
                  id="{{uniqueId}}-show-parking"
                  checked={{this.isFeatureShown "parking"}}
                  {{debounce (this.toggleShowFeature "parking")}}
                />
                <label for="{{uniqueId}}-show-parking" class="form-check-label">
                  Parking Information
                </label>
              </div>

              <div class="form-check mb-2">
                <input
                  type="checkbox"
                  class="form-check-input"
                  id="{{uniqueId}}-show-gardens"
                  checked={{this.isFeatureShown "gardens"}}
                  {{debounce (this.toggleShowFeature "gardens")}}
                />
                <label for="{{uniqueId}}-show-gardens" class="form-check-label">
                  The Gardens
                </label>
              </div>

              <div class="form-check mb-2">
                <input
                  type="checkbox"
                  class="form-check-input"
                  id="{{uniqueId}}-show-dining"
                  checked={{this.isFeatureShown "dining"}}
                  {{debounce (this.toggleShowFeature "dining")}}
                />
                <label for="{{uniqueId}}-show-dining" class="form-check-label">
                  Dining Options
                </label>
              </div>
            </div>

            <div class="col-md-4">
              <div class="form-check mb-2">
                <input
                  type="checkbox"
                  class="form-check-input"
                  id="{{uniqueId}}-show-taxi"
                  checked={{this.isFeatureShown "taxi"}}
                  {{debounce (this.toggleShowFeature "taxi")}}
                />
                <label for="{{uniqueId}}-show-taxi" class="form-check-label">
                  Local Taxi Services
                </label>
              </div>

              <div class="form-check mb-2">
                <input
                  type="checkbox"
                  class="form-check-input"
                  id="{{uniqueId}}-show-facilities"
                  checked={{this.isFeatureShown "facilities"}}
                  {{debounce (this.toggleShowFeature "facilities")}}
                />
                <label
                  for="{{uniqueId}}-show-facilities"
                  class="form-check-label"
                >
                  Facilities &amp; Amenities
                </label>
              </div>
            </div>
          </div>
        </fieldset>

        <fieldset disabled={{isReadOnly}} class="mb-4">
          <legend class="gud-eyebrow">Relationships</legend>

          <div class="mb-3">
            <label class="form-label">Files</label>
            <Await @promise={{this.arrival.files}}>
              <:success as |files|>
                {{#if files.length}}
                  <div class="list-group mb-2">
                    {{#each files as |file|}}
                      <div class="list-group-item">
                        <div
                          class="d-flex justify-content-between align-items-center"
                        >
                          <div>
                            <iconify-icon inline icon="ph:file" />
                            {{file.name}}
                          </div>
                          {{#unless isReadOnly}}
                            <button
                              type="button"
                              class="btn btn-sm btn-outline-danger"
                              {{on "click" (fn this.removeFile file)}}
                            >
                              <iconify-icon inline icon="ph:x-bold" />
                              <span class="visually-hidden">Remove</span>
                            </button>
                          {{/unless}}
                        </div>
                      </div>
                    {{/each}}
                  </div>
                {{else}}
                  <p class="text-muted">No files attached</p>
                {{/if}}
              </:success>
            </Await>
            {{#unless isReadOnly}}
              <FilePicker
                @canSelectFiles={{true}}
                @canSelectFolders={{false}}
                @navigationFolders={{this.navigationFolders}}
                @onSelect={{this.handleFileSelect}}
              >
                <:input as |showModal|>
                  <button
                    type="button"
                    class="btn btn-secondary btn-sm"
                    {{on "click" (fn showModal this.startingFolderId)}}
                  >
                    <iconify-icon inline icon="ph:plus-bold" />
                    Add File
                  </button>
                </:input>
              </FilePicker>
            {{/unless}}
            <div class="form-text">
              Optional: Attach files (e.g., maps, guides) to display on the
              arrivals page.
            </div>
          </div>

          <div class="mb-3">
            <label class="form-label">Calendar Items</label>
            <Await @promise={{this.arrival.calendarItems}}>
              <:success as |calendarItems|>
                {{#if calendarItems.length}}
                  <div class="list-group mb-2">
                    {{#each calendarItems as |calendarItem|}}
                      <div class="list-group-item">
                        <div
                          class="d-flex justify-content-between align-items-center"
                        >
                          <div>
                            <iconify-icon inline icon="ph:calendar" />
                            {{calendarItem.title}}
                          </div>
                          {{#unless isReadOnly}}
                            <button
                              type="button"
                              class="btn btn-sm btn-outline-danger"
                              {{on
                                "click"
                                (fn this.removeCalendarItem calendarItem)
                              }}
                            >
                              <iconify-icon inline icon="ph:x-bold" />
                              <span class="visually-hidden">Remove</span>
                            </button>
                          {{/unless}}
                        </div>
                      </div>
                    {{/each}}
                  </div>
                {{else}}
                  <p class="text-muted">No calendar items selected</p>
                {{/if}}
              </:success>
            </Await>
            {{#unless isReadOnly}}
              <Await @promise={{this.arrival.calendarItems}}>
                <:success as |calendarItems|>
                  <Await @promise={{@cohort.program}}>
                    <:success as |program|>
                      <CalendarItemsPicker
                        @programId={{if program.id program.id ""}}
                        @cohortId={{this.cohortId}}
                        @onSelect={{this.handleCalendarItemsSelect}}
                      >
                        <:input as |showModal|>
                          <button
                            type="button"
                            class="btn btn-secondary btn-sm"
                            {{on "click" (fn showModal (getIds calendarItems))}}
                          >
                            <iconify-icon inline icon="ph:calendar-plus" />
                            Add Events
                          </button>
                        </:input>
                      </CalendarItemsPicker>
                    </:success>
                  </Await>
                </:success>
              </Await>
            {{/unless}}
            <div class="form-text">
              Optional: Link calendar events to show on the arrivals schedule.
            </div>
          </div>

          <div class="mb-3">
            <label class="form-label">Contacts</label>
            <Await @promise={{this.arrival.contacts}}>
              <:success as |contacts|>
                {{#if contacts.length}}
                  <div class="list-group mb-2">
                    {{#each contacts as |contact|}}
                      <div class="list-group-item">
                        <div
                          class="d-flex justify-content-between align-items-center"
                        >
                          <div>
                            <iconify-icon inline icon="ph:user" />
                            {{this.formatContactName contact}}
                          </div>
                          {{#unless isReadOnly}}
                            <button
                              type="button"
                              class="btn btn-sm btn-outline-danger"
                              {{on "click" (fn this.removeContact contact)}}
                            >
                              <iconify-icon inline icon="ph:x-bold" />
                              <span class="visually-hidden">Remove</span>
                            </button>
                          {{/unless}}
                        </div>
                      </div>
                    {{/each}}
                  </div>
                {{else}}
                  <p class="text-muted">No contacts selected</p>
                {{/if}}
              </:success>
            </Await>
            {{#unless isReadOnly}}
              <ContactPicker @onSelect={{this.handleContactSelect}}>
                <:input as |showModal|>
                  <button
                    type="button"
                    class="btn btn-secondary btn-sm"
                    {{on "click" showModal}}
                  >
                    <iconify-icon inline icon="ph:user-plus" />
                    Add Contact
                  </button>
                </:input>
              </ContactPicker>
            {{/unless}}
            <div class="form-text">
              Optional: Add contacts (facilitators, organizers) to display on
              the arrivals page.
            </div>
          </div>
        </fieldset>
      </:form>
    </EditRecord>
  </template>
}
